import { useEffect, useMemo } from "react";
import { Badge, Table } from "react-bootstrap";
import { useContextValues } from "../../context/RouteDataContext.jsx";
import useTableHover from "../../utils/useTableHover";
import TimeBadge from "../TimeBadge.jsx";
import { AppLink } from "../../services/goTo.jsx";
import { TimeProvider } from "../../services/timeProvider.js";

const toArray = (value) => (Array.isArray(value) ? value : Object.values(value || {}));

export default function ScheduleTable({ onLayoutChange }) {
  const { timeTable, continuesAs, selectedStops, routeShortName } = useContextValues();
  const { hoverRow, hoverCol, bindCell } = useTableHover();

  const tripIds = useMemo(() => {
    const allTripIds = new Set();
    const firstDepartureByTrip = new Map();

    timeTable.forEach((stop) => {
      const stopTrips = toArray(stop?.trips);

      stopTrips.forEach((trip) => {
        const tripId = trip?.trip_id;
        if (!tripId) return;

        allTripIds.add(tripId);

        if (firstDepartureByTrip.has(tripId)) return;

        if (trip?.departure_time) {
          // Wrapped after-midnight times sort at the end of the requested service date.
          firstDepartureByTrip.set(tripId, TimeProvider.scheduleSeconds(trip.departure_time));
        }
      });
    });

    return Array.from(allTripIds).sort((a, b) => {
      const aTime = firstDepartureByTrip.get(a);
      const bTime = firstDepartureByTrip.get(b);

      const aSort = Number.isFinite(aTime) ? aTime : Number.POSITIVE_INFINITY;
      const bSort = Number.isFinite(bTime) ? bTime : Number.POSITIVE_INFINITY;

      if (aSort !== bSort) {
        return aSort - bSort;
      }

      return String(a).localeCompare(String(b));
    });
  }, [timeTable]);


  // Notify parent container of layout changes
  useEffect(() => {
    onLayoutChange?.();
  }, [timeTable]);


  if (!timeTable || timeTable.length === 0) {
    return;
  }

  return (

    <Table responsive className="ict">
      <thead>
        <tr className="visually-hidden">
          <th scope="col" className="sticky z-10">
            Stop
          </th>
          {tripIds.map((id) => (
            <th scope="col" key={id} className="sticky">
              <div className="small text-muted">
                {continuesAs?.[id]
                  ? `Continues as Route ${continuesAs[id].route_short_name}`
                  : "No Interline."}
              </div>
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {timeTable.map((stop, rowIndex) => {
          const rowClass = [
            selectedStops?.length && !selectedStops.includes(stop.stop_id) ? "hidden" : "",
            stop.timepoint ? "timepoint" : "",
          ].join(" ");

          const stopTrips = toArray(stop.trips);
          const stopLink = `/plan-your-trip/stops/${stop.stop_id}`;

          return (
            <tr
              key={`${stop.stop_id}-${stop.ict_custom__loop_pass}-${rowIndex}`}
              className={`${rowClass} ${hoverRow === rowIndex ? "row-hover" : ""}`}
              data-stop-id={stop.stop_id}
            >
              <td
                className={`sticky z-10 ${hoverRow === rowIndex || hoverCol === 0 ? "cell-hover" : ""
                  }`}
                {...bindCell(rowIndex, 0)}
              >
                <div>
                  <AppLink to={stopLink} title={`View all details for stop #${stop.stop_id}`}>
                    {stop.stop_name}{" "}
                  </AppLink>
                </div>
              </td>

              {tripIds.map((tripId, colIndex) => {
                const trip = stopTrips.find((t) => t.trip_id === tripId) || null;
                const col = colIndex + 1;

                return (
                  <td
                    key={`${stop.stop_id}-${tripId}`}
                    className={`${hoverRow === rowIndex || hoverCol === col ? "cell-hover" : ""
                      }`}
                    {...bindCell(rowIndex, col)}
                  >
                    {trip?.departure_time ? <TimeBadge value={trip.departure_time} /> : "-"}
                  </td>
                );
              })}
            </tr>
          );
        })}

        <tr className="continues-as">
          <th style={{ textAlign: "right", fontStyle: "italic" }}>
            Continues as:
          </th>
          {tripIds.map((id) => (
            <td key={id}>
              {continuesAs?.[id] ? (<AppLink
                to={`/plan-your-trip/routes/${continuesAs[id].route_short_name}`}
                className="short-name"
              ><Badge>
                  {continuesAs[id].route_short_name}
                </Badge>
              </AppLink>
              ) : (
                <Badge>
                  {routeShortName || "N/A"}
                </Badge>
              )}
            </td>
          ))}
        </tr>
      </tbody>
    </Table>
  );
}
